import { daysSince } from '../../utils/date-utils.ts'

/**
 * How current the estate's threat models are.
 *
 * Separate from `org-threat-posture.ts` because it comes from a different endpoint. Posture is derived
 * from each model's *content*; freshness is metadata on the model itself, which means a second request
 * per repository. Keeping the two aggregates apart keeps that cost visible rather than hidden inside one
 * innocuous-looking function.
 *
 * **This is the only figure on the page that is not a generated judgment.** Exposure, relevance and
 * control coverage are all things a model asserted about a system; `generatedAt` is a timestamp we wrote.
 * It is also what every other figure silently depends on — a model nobody has regenerated argues
 * confidently for a system that no longer exists.
 *
 * **Age is a proxy, and must read as one.** The signal a reader actually wants is commit drift: was this
 * model generated against a commit the default branch has since moved past? That is not derivable —
 * `Repository` carries `default_branch` as a name and no tip sha — so this measures wall-clock age
 * instead. A model of a dormant repository is not stale; this will call it stale anyway.
 */

/** Ordered freshest to stalest, so anything presenting these as a series gets the order for free. */
export const FRESHNESS_BANDS = ['week', 'month', 'stale', 'undated'] as const
export type FreshnessBand = (typeof FRESHNESS_BANDS)[number]

/**
 * 30 days is the line, chosen by Terra over a more generous 90.
 *
 * Everything past it is one band rather than several. How stale a stale model is does not change what to
 * do about it, and splitting it would invent a gradation nobody asked to see.
 */
const STALE_AFTER_DAYS = 30
const RECENT_WITHIN_DAYS = 7
/** How far ahead of `now` a timestamp may sit before it reads as wrong rather than as new. */
const FUTURE_TOLERANCE_DAYS = 1

export type FreshnessInput = {
  repositoryId: string
  /** Absent when the model exists but the response carried no timestamp — not the same as no model. */
  generatedAt?: string
}

export type EstateFreshness = {
  /** Models that answered, banded freshest first. Empty bands are reported, never dropped. */
  bands: { band: FreshnessBand; modelCount: number }[]
  /**
   * Age per repository, for the ones that reported a date.
   *
   * Lets a per-repository view caveat its own rows: a repository ranked worst-covered on the strength of
   * a year-old document is a different claim from the same ranking on a document generated this week, and
   * the estate band cannot say which is which.
   */
  ageInDaysByRepository: Record<string, number>
  /**
   * The band each repository's model falls in.
   *
   * Recorded here rather than re-derived at the call site so a per-row label and the estate roll-up
   * cannot disagree about where the boundary sits.
   */
  bandByRepository: Record<string, FreshnessBand>
  /** Models that answered at all — the denominator every band is a share of. */
  modelCount: number
  /** Past {@link STALE_AFTER_DAYS}. The one number worth acting on, and the alarm figure. */
  staleCount: number
  /** Both thresholds travel with the summary so labels and copy state them rather than repeating them. */
  staleAfterDays: number
  recentWithinDays: number
}

/**
 * Built from the thresholds rather than typed, so moving the stale line moves the labels with it.
 *
 * `undated` reads as "Unknown" rather than naming the missing field: from a reader's side the model's age
 * is simply not known, and why the response omitted a timestamp is not their problem.
 */
export const freshnessBandLabels = ({
  recentWithinDays,
  staleAfterDays,
}: {
  recentWithinDays: number
  staleAfterDays: number
}): Record<FreshnessBand, string> => ({
  week: `Last ${recentWithinDays} days`,
  month: `Last ${staleAfterDays} days`,
  stale: 'Stale',
  undated: 'Unknown',
})

const bandFor = ({ generatedAt, now }: { generatedAt?: string; now: string }): FreshnessBand => {
  if (generatedAt === undefined) {
    return 'undated'
  }

  const age = daysSince(generatedAt, now)
  // An unparseable timestamp is undated, not fresh. Guessing young would hide exactly the models a
  // reader is looking for.
  if (age === undefined) {
    return 'undated'
  }

  // A timestamp meaningfully ahead of `now` is not evidence of freshness, for the same reason an
  // unparseable one is not: banding it `week` would rank a mis-stamped or backdated model as the
  // newest thing in the estate, which is the opposite of what a reader scanning for stale models
  // needs. The day of tolerance absorbs ordinary clock skew between the generator and the browser —
  // `daysSince` floors, so even a few seconds ahead already reports -1.
  if (age < -FUTURE_TOLERANCE_DAYS) {
    return 'undated'
  }

  if (age > STALE_AFTER_DAYS) {
    return 'stale'
  }

  return age <= RECENT_WITHIN_DAYS ? 'week' : 'month'
}

/**
 * Bands the models that answered. Repositories with no model at all are not passed in — they are
 * `withoutModel` in the posture aggregate, and folding them in here would report a stale estate for one
 * that has simply never been looked at.
 */
export const summariseFreshness = ({
  models,
  now,
}: {
  models: readonly FreshnessInput[]
  now: string
}): EstateFreshness => {
  const bands = FRESHNESS_BANDS.map(band => ({
    band,
    modelCount: models.filter(model => bandFor({ generatedAt: model.generatedAt, now }) === band).length,
  }))

  const bandByRepository = models.reduce<Record<string, FreshnessBand>>((bands_, model) => {
    bands_[model.repositoryId] = bandFor({ generatedAt: model.generatedAt, now })
    return bands_
  }, {})

  const ageInDaysByRepository = models.reduce<Record<string, number>>((ages, model) => {
    const age = model.generatedAt === undefined ? undefined : daysSince(model.generatedAt, now)
    if (age !== undefined) {
      ages[model.repositoryId] = age
    }
    return ages
  }, {})

  return {
    bands,
    ageInDaysByRepository,
    bandByRepository,
    modelCount: models.length,
    staleCount: bands.find(band => band.band === 'stale')?.modelCount ?? 0,
    staleAfterDays: STALE_AFTER_DAYS,
    recentWithinDays: RECENT_WITHIN_DAYS,
  }
}
